import React, { useEffect, useState } from 'react';
import { backtestScenariosService, BacktestScenario } from '../../services/backtestScenariosService';
import { ScenarioPicker } from './ScenarioPicker';
import { ScenarioCard } from './ScenarioCard';

interface BacktestPerformanceTabProps {
  token: string;
}

export const BacktestPerformanceTab: React.FC<BacktestPerformanceTabProps> = ({ token }) => {
  const [scenarios, setScenarios] = useState<BacktestScenario[]>([]);
  const [activeScenarioId, setActiveScenarioId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    backtestScenariosService
      .list(token)
      .then((data) => {
        if (cancelled) return;
        setScenarios(data);
        if (data.length > 0) {
          setActiveScenarioId(data[0].scenario_id);
        }
      })
      .catch((err: any) => {
        if (cancelled) return;
        const msg = err.response?.data?.error || err.message || 'Failed to load backtest scenarios';
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <p style={{ color: '#94a3b8' }}>Loading backtest scenarios...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: 'rgba(239,68,68,0.15)',
        border: '1px solid rgba(239,68,68,0.3)',
        borderRadius: '8px',
        padding: '1rem',
        color: '#f87171',
        marginTop: '1rem',
      }}>
        <strong>Error:</strong> {error}
      </div>
    );
  }

  if (scenarios.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '3rem',
        backgroundColor: 'rgba(255,255,255,0.05)',
        borderRadius: '8px',
        marginTop: '1rem',
      }}>
        <p style={{ color: '#94a3b8' }}>No backtest scenarios are available yet.</p>
      </div>
    );
  }

  const active = scenarios.find((s) => s.scenario_id === activeScenarioId) || scenarios[0];

  return (
    <div style={{ marginTop: '1.5rem' }}>
      {/* Intro copy */}
      <p style={{ color: '#94a3b8', fontSize: '0.9em', margin: '0 0 1.25rem 0', lineHeight: 1.6 }}>
        We replayed every published signal through historical market data to measure
        how the strategy would have performed under different management rules.
        Select a scenario below to explore the results.
      </p>

      <ScenarioPicker
        scenarios={scenarios}
        activeScenarioId={active.scenario_id}
        onSelect={setActiveScenarioId}
      />

      <ScenarioCard scenario={active} />
    </div>
  );
};
